/**
 * Default print configuration for POS receipts
 * Defines layout, positioning, and dimensions for receipt printing
 */

export interface FieldPosition {
  top: string;
  left: string;
  visible: boolean;
}

export type PosPrintFieldKey =
  | "store_name"
  | "store_address"
  | "invoice_number"
  | "date_time"
  | "cashier_name"
  | "customer_name"
  | "transaction_type"
  | "payment_method"
  | "subtotal"
  | "discount_label"
  | "discount_amount"
  | "total_amount";

export interface PosPrintConfig {
  page: {
    width: string;
    height: string;
  };

  layout: {
    left_margin: string;
    right_margin: string;
  };

  sections: {
    header: {
      top: string;
      left: string;
      height: string;
    };
    table: {
      top: string;
      left: string;
      width: string;
      row_height: string;
    };
    footer: {
      top: string;
      height: string;
    };
  };

  fields: Record<PosPrintFieldKey, FieldPosition>;

  tableMeta?: {
    bottom_padding?: string | number;
    footer_height?: string | number;
  };
}

const baseFieldPositions: Record<PosPrintFieldKey, Omit<FieldPosition, "visible">> = {
  store_name: { top: "0.5cm", left: "0.5cm" },
  store_address: { top: "1.2cm", left: "0.5cm" },
  invoice_number: { top: "2.0cm", left: "0.5cm" },
  date_time: { top: "2.6cm", left: "0.5cm" },
  cashier_name: { top: "3.2cm", left: "0.5cm" },
  customer_name: { top: "3.8cm", left: "0.5cm" },
  transaction_type: { top: "4.4cm", left: "0.5cm" },
  payment_method: { top: "5.0cm", left: "0.5cm" },
  subtotal: { top: "0.5cm", left: "7.5cm" },
  discount_label: { top: "1.1cm", left: "7.5cm" },
  discount_amount: { top: "1.1cm", left: "9.5cm" },
  total_amount: { top: "1.7cm", left: "7.5cm" },
};

const fieldsWithDefaultVisibility = Object.fromEntries(
  Object.entries(baseFieldPositions).map(([key, value]) => [
    key,
    { ...value, visible: true },
  ])
) as Record<PosPrintFieldKey, FieldPosition>;

export const defaultPosPrintConfig: PosPrintConfig = {
  page: {
    width: "8cm",
    height: "auto",
  },

  layout: {
    left_margin: "0.3cm",
    right_margin: "0.3cm",
  },

  sections: {
    header: {
      top: "0.3cm",
      left: "0.3cm",
      height: "5.2cm",
    },
    table: {
      top: "5.5cm",
      left: "0.3cm",
      width: "7.4cm",
      row_height: "0.6cm",
    },
    footer: {
      top: "27.5cm",
      height: "2.2cm",
    },
  },

  fields: fieldsWithDefaultVisibility,

  tableMeta: {
    bottom_padding: 0.5,
    footer_height: 1.2,
  },
};
